import { useCallback, useRef } from 'react';
import { CsvExportActionLifecycleArgs } from '@/components/action-buttons/csv-export-action.types';
import { useCsvExportActionCore } from '@/components/action-buttons/use-csv-export-action-core';

export type MarketplaceCsvExportResult = {
    limitWarning?: string | null;
};

export type MarketplaceCsvDownloadArgs = {
    accessToken: string;
    exportOperationId: string;
    signal?: AbortSignal;
};

type UseMarketplaceCsvExportActionArgs = CsvExportActionLifecycleArgs & {
    accessToken: string;
    runCsvDownload: (args: MarketplaceCsvDownloadArgs) => Promise<MarketplaceCsvExportResult>;
};

type UseMarketplaceCsvExportActionResult = {
    exportCsv: () => Promise<void>;
    cancelExport: () => void;
};

export const useMarketplaceCsvExportAction = ({
    accessToken,
    isExportLoading,
    startExport,
    finishExportSuccess,
    finishExportFailure,
    finishExportCanceled,
    runCsvDownload,
}: UseMarketplaceCsvExportActionArgs): UseMarketplaceCsvExportActionResult => {
    const activeExportAbortControllerRef = useRef<AbortController | null>(null);
    const activeExportOperationIdRef = useRef<string | null>(null);

    const runExport = useCallback(
        async (exportOperationId: string) => {
            const abortController = new AbortController();
            activeExportAbortControllerRef.current = abortController;
            activeExportOperationIdRef.current = exportOperationId;

            try {
                return await runCsvDownload({
                    accessToken,
                    exportOperationId,
                    signal: abortController.signal,
                });
            } finally {
                if (activeExportOperationIdRef.current === exportOperationId) {
                    activeExportOperationIdRef.current = null;
                    activeExportAbortControllerRef.current = null;
                }
            }
        },
        [accessToken, runCsvDownload],
    );

    const { exportCsv } = useCsvExportActionCore({
        isExportLoading,
        startExport,
        finishExportSuccess,
        finishExportFailure,
        finishExportCanceled,
        runExport,
    });

    const cancelExport = useCallback(() => {
        const abortController = activeExportAbortControllerRef.current;
        if (!abortController) {
            return;
        }

        abortController.abort();
    }, []);

    return {
        exportCsv,
        cancelExport,
    };
};
